import { useCallback, useMemo, useState } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"
import { useTranslation } from "react-i18next"

import type { FlowPluginBindings } from "@/api/plugins-api"
import type { NodeProps } from "@xyflow/react"

import { Bot, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { FlowPluginPicker } from "@/features/plugins/flow-plugin-picker"
import { useAvailableGPTModels } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { KNOWN_SESSION_TYPES } from "@/types"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { FlowPromptSelector } from "../flow-prompt-selector"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { WorkflowModelPicker } from "../workflow-model-picker"
import { AgentToolItem } from "./agent-tool-item"
import { CollapsibleToolsHeader } from "./collapsible-tools-header"
import { LibraryPicker } from "./library-picker"
import { M365ActionPicker } from "./m365-action-picker"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"
import { TrustedSourcesPicker } from "./trusted-sources-picker"

const AVAILABLE_TOOLS = [
  { id: "web_search", translationKey: "web_search", descriptionKey: "toolDescriptions.web_search" },
  {
    id: "file_search",
    translationKey: "file_search",
    descriptionKey: "toolDescriptions.file_search",
  },
  {
    id: "library_search",
    translationKey: "library_search",
    descriptionKey: "toolDescriptions.library_search",
  },
  {
    id: "flow_attachment_search",
    translationKey: "flow_attachment_search",
    descriptionKey: "toolDescriptions.flow_attachment_search",
  },
  { id: "web_scrape", translationKey: "web_scrape", descriptionKey: "toolDescriptions.web_scrape" },
  {
    id: "microsoft_365_search",
    translationKey: "microsoft_365_search",
    descriptionKey: "toolDescriptions.microsoft_365_search",
  },
]

// Microsoft 365 action options (includes send_email and create_calendar_event for agent automation)
// Note: tool-node.tsx only includes the 5 read-only actions
const M365_ACTIONS = [
  { value: "get_emails", labelKey: "m365Picker.actions.get_emails" },
  { value: "list_shared_mailboxes", labelKey: "m365Picker.actions.list_shared_mailboxes" },
  { value: "get_calendar_events", labelKey: "m365Picker.actions.get_calendar_events" },
  { value: "search_teams", labelKey: "m365Picker.actions.search_teams" },
  { value: "get_files", labelKey: "m365Picker.actions.get_files" },
  { value: "get_meeting_insights", labelKey: "m365Picker.actions.get_meeting_insights" },
  { value: "send_email", labelKey: "m365Picker.actions.send_email" },
  { value: "create_calendar_event", labelKey: "m365Picker.actions.create_calendar_event" },
] as const

export function AgentNode({ id, data, selected }: NodeProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.agentNode" })
  const {
    isError: modelsError,
    isLoading: modelsLoading,
    models,
  } = useAvailableGPTModels(KNOWN_SESSION_TYPES.Explorer)
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)
  const [isToolsExpanded, setIsToolsExpanded] = useState(false)

  const enabledTools: string[] = Array.isArray(data.enabledTools) ? data.enabledTools : []
  const storedLibraries: string[] = Array.isArray(data.selectedLibraries)
    ? data.selectedLibraries
    : []

  // Trusted sources state
  const selectedTrustedSourceUrls: string[] = Array.isArray(data.selectedTrustedSources)
    ? data.selectedTrustedSources
    : []
  const trustedMode: "only" | "preferred" = data.trustedMode === "preferred" ? "preferred" : "only"

  // M365 actions state
  const selectedM365Actions: string[] = Array.isArray(data.m365Actions)
    ? data.m365Actions
    : M365_ACTIONS.filter(
        (a) => a.value !== "send_email" && a.value !== "create_calendar_event"
      ).map((a) => a.value)

  const availableLibraries = useMemo(() => {
    const settingsService = SettingsService.getInstance()
    return settingsService.settings.indexes
      .filter((index) => {
        // Include libraries unless "flow" is in blocked_modules
        if (!index.blocked_modules || index.blocked_modules.length === 0) return true
        return !index.blocked_modules.includes("flow")
      })
      .map((index) => ({
        id: index.id,
        name: index.display_name,
        description: index.description,
      }))
  }, [])

  // Default to all libraries selected when none are explicitly chosen
  const selectedLibraries =
    storedLibraries.length > 0 ? storedLibraries : availableLibraries.map((lib) => lib.id)

  const toggleLibrary = useCallback(
    (libraryId: string) => {
      const isActive = selectedLibraries.includes(libraryId)
      const newSelection = isActive
        ? selectedLibraries.filter((id) => id !== libraryId)
        : [...selectedLibraries, libraryId]
      updateNodeData(id, { selectedLibraries: newSelection })
    },
    [id, selectedLibraries, updateNodeData]
  )

  const toggleTrustedSource = useCallback(
    (sourceUrl: string) => {
      const isActive = selectedTrustedSourceUrls.includes(sourceUrl)
      const newSelection = isActive
        ? selectedTrustedSourceUrls.filter((url) => url !== sourceUrl)
        : [...selectedTrustedSourceUrls, sourceUrl]
      updateNodeData(id, { selectedTrustedSources: newSelection })
    },
    [id, selectedTrustedSourceUrls, updateNodeData]
  )

  const handleTrustedModeChange = useCallback(
    (mode: "only" | "preferred") => {
      updateNodeData(id, { trustedMode: mode })
    },
    [id, updateNodeData]
  )

  const toggleM365Action = useCallback(
    (actionValue: string) => {
      const isActive = selectedM365Actions.includes(actionValue)
      const newSelection = isActive
        ? selectedM365Actions.filter((a) => a !== actionValue)
        : [...selectedM365Actions, actionValue]
      updateNodeData(id, { m365Actions: newSelection })
    },
    [id, selectedM365Actions, updateNodeData]
  )

  const handleSystemPromptChange = useCallback(
    (value: string) => {
      updateNodeData(id, { systemPrompt: value })
    },
    [id, updateNodeData]
  )

  const handlePromptSelect = useCallback(
    (content: string) => {
      updateNodeData(id, { systemPrompt: content })
    },
    [id, updateNodeData]
  )

  const handleMaxIterationsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Math.max(2, parseInt(e.target.value) || 2)
      updateNodeData(id, { maxIterations: value })
    },
    [id, updateNodeData]
  )

  const handleToolToggle = useCallback(
    (toolId: string, checked: boolean) => {
      const newTools = checked
        ? [...enabledTools, toolId]
        : enabledTools.filter((t) => t !== toolId)
      updateNodeData(id, { enabledTools: newTools })
    },
    [id, enabledTools, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const hasOutput = isCompleted && !!output
  const renderPromptEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">{t("systemPrompt")}</Label>
          <FlowPromptSelector onPromptSelect={handlePromptSelect} />
        </div>
        <TemplateField
          multiline
          placeholder={t("systemPromptPlaceholder")}
          value={(data.systemPrompt as string) || ""}
          className={cn(
            "nowheel overflow-auto pr-8",
            isModal ? "min-h-[360px]" : "max-h-[100px] min-h-[50px]"
          )}
          onChange={handleSystemPromptChange}
        />
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
    </div>
  )
  const renderModelControls = (isModal = false) => (
    <div className={cn("grid gap-2", isModal ? "sm:grid-cols-2" : "grid-cols-2")}>
      <div className="grid gap-1.5">
        <Label className="text-xs">{t("model")}</Label>
        <WorkflowModelPicker
          availableModels={models}
          hasCatalogError={modelsError}
          isCatalogLoading={modelsLoading}
          modelId={data.model}
          reasoningEffort={data.reasoningEffort}
          onChange={(patch) => updateNodeData(id, patch)}
        />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">{t("maxIterations")}</Label>
        <Input
          className="nodrag h-7 text-xs"
          min={2}
          type="number"
          value={(data.maxIterations as number) || 10}
          onChange={handleMaxIterationsChange}
        />
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[300px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[1])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Bot className="size-4" />
            {t("title")}
            <NodeEditDialog
              description="Edit this agent node in a larger workspace."
              title={t("title")}
            >
              {renderPromptEditor(true)}
              {renderModelControls(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          <div className="grid gap-3">
            {renderPromptEditor()}

            {renderModelControls()}
            <FlowPluginPicker
              value={(data.plugin_bindings as FlowPluginBindings | undefined) ?? {}}
              onChange={(bindings) => {
                const existing = enabledTools.filter(
                  (name) =>
                    !["plugin_catalog", "plugin_operations", "plugin_execute"].includes(name)
                )
                updateNodeData(id, {
                  plugin_bindings: bindings,
                  enabledTools: Object.keys(bindings).length
                    ? [...existing, "plugin_catalog", "plugin_operations", "plugin_execute"]
                    : existing,
                })
              }}
            />

            {/* Collapsible tools section */}
            <Collapsible open={isToolsExpanded} onOpenChange={setIsToolsExpanded}>
              <div className="grid gap-1.5">
                <CollapsibleToolsHeader
                  isExpanded={isToolsExpanded}
                  toolCount={enabledTools.length}
                />
                <CollapsibleContent>
                  <ScrollArea className="nodrag nowheel h-[100px] rounded border p-2">
                    <div className="grid gap-2">
                      {AVAILABLE_TOOLS.map((tool) => {
                        let configComponent: React.ReactNode = null

                        if (
                          tool.id === "library_search" &&
                          enabledTools.includes("library_search")
                        ) {
                          configComponent = (
                            <LibraryPicker
                              availableLibraries={availableLibraries}
                              isUsingDefault={storedLibraries.length === 0}
                              selectedLibraries={selectedLibraries}
                              onToggleLibrary={toggleLibrary}
                            />
                          )
                        } else if (
                          tool.id === "web_search" &&
                          enabledTools.includes("web_search")
                        ) {
                          configComponent = (
                            <TrustedSourcesPicker
                              selectedUrls={selectedTrustedSourceUrls}
                              trustedMode={trustedMode}
                              onModeChange={handleTrustedModeChange}
                              onToggleSource={toggleTrustedSource}
                            />
                          )
                        } else if (
                          tool.id === "microsoft_365_search" &&
                          enabledTools.includes("microsoft_365_search")
                        ) {
                          configComponent = (
                            <M365ActionPicker
                              actions={M365_ACTIONS}
                              selectedActions={selectedM365Actions}
                              onToggleAction={toggleM365Action}
                            />
                          )
                        }

                        return (
                          <AgentToolItem
                            key={tool.id}
                            configComponent={configComponent}
                            isEnabled={enabledTools.includes(tool.id)}
                            nodeId={id}
                            tool={tool}
                            onToggle={handleToolToggle}
                          />
                        )
                      })}
                    </div>
                  </ScrollArea>
                </CollapsibleContent>
              </div>
            </Collapsible>

            <div className="grid gap-2 border-t pt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!data.showOutput}
                  className="nodrag"
                  id={`${id}-showOutput`}
                  onCheckedChange={handleShowOutputChange}
                />
                <Label className="cursor-pointer text-xs font-normal" htmlFor={`${id}-showOutput`}>
                  {t("showOutput")}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={data.showInActivityHistory !== false}
                  className="nodrag"
                  id={`${id}-showInActivityHistory`}
                  onCheckedChange={handleShowInActivityHistoryChange}
                />
                <Label
                  className="cursor-pointer text-xs font-normal"
                  htmlFor={`${id}-showInActivityHistory`}
                >
                  {t("showInActivityHistory")}
                </Label>
              </div>
            </div>
          </div>
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
